import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

type StepFirstSubsectionExpandContextValue = {
  /** First collapsible subsection in scope claims expanded; subsequent calls return false. */
  claimFirst: () => boolean;
};

const StepFirstSubsectionExpandContext =
  createContext<StepFirstSubsectionExpandContextValue | null>(null);

/**
 * Scopes "first subsection expanded by default" to one step (or tab) body.
 * Remount with `key={stepId}` (or tab id) so the claim resets per navigation.
 */
export function StepFirstSubsectionExpandProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const claimedRef = useRef(false);

  const claimFirst = useCallback(() => {
    if (claimedRef.current) {
      return false;
    }
    claimedRef.current = true;
    return true;
  }, []);

  const value = useMemo(() => ({ claimFirst }), [claimFirst]);

  return (
    <StepFirstSubsectionExpandContext.Provider value={value}>
      {children}
    </StepFirstSubsectionExpandContext.Provider>
  );
}

export function useStepFirstSubsectionExpand(): StepFirstSubsectionExpandContextValue | null {
  return useContext(StepFirstSubsectionExpandContext);
}

/** Resolve default expand: explicit prop wins; else first collapsible card in scope. */
export function useResolvedDefaultSectionExpanded(
  isCollapsibleSection: boolean,
  defaultSectionExpandedProp: boolean | undefined,
): boolean {
  const ctx = useContext(StepFirstSubsectionExpandContext);
  const isFirstRef = useRef<boolean | null>(null);

  if (defaultSectionExpandedProp !== undefined) {
    return defaultSectionExpandedProp;
  }
  if (!isCollapsibleSection) {
    return true;
  }
  if (isFirstRef.current === null) {
    isFirstRef.current = ctx?.claimFirst() ?? false;
  }
  return isFirstRef.current;
}
